import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Calendar, ChevronLeft, ChevronRight, Plus, AlertTriangle,
  Clock, Car, User as UserIcon, X, Check, Loader2, Search, Lock, Trash2, MessageSquareText,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VehiculePicker from "@/components/VehiculePicker";
import { useAuth } from "@/contexts/AuthContext";
import QuickClientDialog from "@/components/QuickClientDialog";
import QuickVehiculeDialog from "@/components/QuickVehiculeDialog";
import PriseEnChargeModal from "@/components/PriseEnChargeModal";
import DemarrerOrModal from "@/components/DemarrerOrModal";

const JOURS_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const STATUT_BADGE = {
  planifie: "bg-blue-50 text-blue-700 border-blue-200",
  confirme: "bg-green-50 text-green-700 border-green-200",
  annule: "bg-gray-100 text-gray-600 border-gray-200 line-through",
  realise: "bg-emerald-50 text-emerald-700 border-emerald-200",
  no_show: "bg-red-50 text-red-700 border-red-200",
};
const STATUT_LABELS = {
  planifie: "Planifié",
  confirme: "Confirmé",
  annule: "Annulé",
  realise: "Réalisé",
  no_show: "Absent",
};

const BLOCAGE_LABELS = {
  total: "Journée bloquée",
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  rapide: "Rapide",
};

function fmtDate(iso, opts) {
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-CH", opts || { day: "2-digit", month: "short" });
}

function toIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

// iter291 — horodatage complet (envoi de SMS), pas une date « AAAA-MM-JJ »
function fmtHorodatage(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

function ClientSearchPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  useEffect(() => {
    if (!query) { setResults([]); setSearched(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query } });
        setResults(data.clients || []);
        setSearched(true);
      } catch (e) { setResults([]); setSearched(true); }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  if (value) {
    return (
      <div className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-md bg-gray-50">
        <UserIcon className="w-4 h-4 text-[#52a675]" />
        <div className="flex-1 text-sm">
          <span className="font-semibold">{value.nom}</span>
          <span className="text-gray-500 ml-2">{value.tel_principal}</span>
        </div>
        <button onClick={() => onChange(null)} className="text-gray-400 hover:text-red-600" data-testid="rdv-clear-client">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Rechercher un client (nom, plaque, tél)…"
          className="pl-10"
          data-testid="rdv-client-search"
        />
      </div>
      {open && query && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {results.map((c) => {
            const nom = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
            return (
              <button
                key={c.id}
                onClick={() => {
                  onChange({ id: c.id, nom, tel_principal: c.tel_principal });
                  setQuery(""); setOpen(false); setResults([]);
                }}
                className="w-full text-left px-3 py-2 hover:bg-[#f0f9f4] text-sm border-b border-gray-50 last:border-0"
                data-testid={`rdv-pick-client-${c.id}`}
              >
                <div className="font-semibold">{nom}</div>
                <div className="text-xs text-gray-500">{c.tel_principal} · {c.localite}</div>
              </button>
            );
          })}
          {searched && results.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500 border-b border-gray-50">
              Aucun client trouvé pour « <span className="font-semibold">{query}</span> »
            </div>
          )}
          <button
            onClick={() => { setQuickOpen(true); setOpen(false); }}
            className="w-full text-left px-3 py-2.5 text-sm bg-[#f0f9f4] hover:bg-[#52a675]/15 text-[#3d8a5c] font-semibold flex items-center gap-2"
            data-testid="rdv-quick-create-client"
          >
            <Plus className="w-4 h-4" />
            Créer un nouveau client « {query} »
          </button>
        </div>
      )}
      <QuickClientDialog
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        initialQuery={query}
        onCreated={(c) => {
          onChange(c);
          setQuery("");
          setResults([]);
        }}
      />
    </div>
  );
}

function RdvDialog({ open, onClose, defaultDate, onSaved, editing, onDemandeConfirmation }) {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [form, setForm] = useState({
    client: null,
    vehicule_id: "",
    type_intervention_id: "",
    date: defaultDate || toIsoDate(new Date()),
    heure_debut: "09:00",
    heures_travail: "",
    jours_immobilisation: "",
    notes: "",
    urgence: false,
    mecanicien_id: "",
  });
  const [dispo, setDispo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quickVehOpen, setQuickVehOpen] = useState(false);

  // Réservation véhicule de remplacement
  const [remplacement, setRemplacement] = useState({
    enabled: false,
    flotte_id: "",
    duree_jours: 1,
    commentaire: "",
    reservation_id: null,  // existant si edition
  });
  const [dispoFlotte, setDispoFlotte] = useState([]);
  const [loadingDispo, setLoadingDispo] = useState(false);
  const [allFlotte, setAllFlotte] = useState([]);
  const [conflitFlotte, setConflitFlotte] = useState(null);  // {flotte_id, suggestion}

  // Reset when opened
  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          client: { id: editing.client_id, nom: editing.client_nom },
          vehicule_id: editing.vehicule_id || "",
          type_intervention_id: editing.type_intervention_id,
          date: editing.date,
          heure_debut: editing.heure_debut,
          heures_travail: editing.heures_travail ?? "",
          jours_immobilisation: editing.jours_immobilisation ?? "",
          notes: editing.notes || "",
          urgence: false,
          mecanicien_id: editing.mecanicien_id || "",
        });
        // Si réservation existante, on la précharge
        if (editing.reservation_id) {
          setRemplacement({
            enabled: true,
            flotte_id: "",
            duree_jours: 1,
            commentaire: "",
            reservation_id: editing.reservation_id,
            label: editing.reservation_label,
          });
        } else {
          setRemplacement({ enabled: false, flotte_id: "", duree_jours: 1, commentaire: "", reservation_id: null });
        }
      } else {
        setForm({
          client: null,
          vehicule_id: "",
          type_intervention_id: "",
          date: defaultDate || toIsoDate(new Date()),
          heure_debut: "09:00",
          heures_travail: "",
          jours_immobilisation: "",
          notes: "",
          urgence: false,
          mecanicien_id: "",
        });
        setRemplacement({ enabled: false, flotte_id: "", duree_jours: 1, commentaire: "", reservation_id: null });
      }
      setDispo(null);
      setConflitFlotte(null);
    }
  }, [open, defaultDate, editing]);

  // Load types once
  useEffect(() => {
    api.get("/types-intervention?actif_only=true")
      .then((r) => setTypes(r.data))
      .catch(() => {});
  }, []);

  // Load vehicules when client selected
  useEffect(() => {
    if (!form.client?.id) { setVehicules([]); return; }
    api.get(`/clients/${form.client.id}`).then((r) => setVehicules(r.data.vehicules || [])).catch(() => {});
  }, [form.client?.id]);

  // Check dispo whenever date or type changes
  useEffect(() => {
    if (!form.date || !form.type_intervention_id) { setDispo(null); return; }
    api.get("/calendrier/dispo", { params: { date: form.date, type_intervention_id: form.type_intervention_id } })
      .then((r) => setDispo(r.data))
      .catch(() => setDispo(null));
  }, [form.date, form.type_intervention_id]);

  // Auto-défaut des durées en fonction de la catégorie du type sélectionné
  const selectedType = types.find((t) => t.id === form.type_intervention_id);
  useEffect(() => {
    if (!selectedType || editing) return;
    if (form.heures_travail === "" && selectedType.duree_min) {
      setForm((f) => ({ ...f, heures_travail: (selectedType.duree_min / 60).toFixed(1) }));
    }
    if (form.jours_immobilisation === "") {
      const defaut = selectedType.categorie === "carrosserie" ? 3 : 0;
      setForm((f) => ({ ...f, jours_immobilisation: defaut }));
    }
  }, [selectedType?.id]); // eslint-disable-line

  // La durée par défaut de la réservation = jours_immobilisation
  useEffect(() => {
    const jours = parseInt(form.jours_immobilisation || 0, 10);
    if (jours > 0 && remplacement.duree_jours !== jours && !remplacement.reservation_id) {
      setRemplacement((r) => ({ ...r, duree_jours: jours }));
    }
  }, [form.jours_immobilisation]); // eslint-disable-line

  // Calcule dates ISO début/fin de la réservation
  const getResaPeriod = () => {
    const start = `${form.date}T${form.heure_debut}:00`;
    const startDate = new Date(start);
    const endDate = new Date(startDate.getTime() + remplacement.duree_jours * 24 * 60 * 60 * 1000);
    // ISO sans Z (heure locale)
    return {
      date_debut: start,
      date_fin: endDate.toISOString().slice(0, 19),
    };
  };

  // Charge les véhicules dispo quand la checkbox est activée OU dates changent
  useEffect(() => {
    if (!remplacement.enabled || remplacement.reservation_id || !form.date || !form.heure_debut) {
      setDispoFlotte([]); return;
    }
    setLoadingDispo(true);
    const period = getResaPeriod();
    Promise.all([
      api.get("/reservations/flotte-disponible", { params: period }),
      api.get("/flotte"),
    ])
      .then(([dispo, all]) => {
        setDispoFlotte(dispo.data || []);
        setAllFlotte(all.data || []);
        // si flotte_id sélectionné mais plus dispo
        if (remplacement.flotte_id && !(dispo.data || []).some((f) => f.id === remplacement.flotte_id)) {
          setRemplacement((r) => ({ ...r, flotte_id: "" }));
        }
      })
      .catch(() => setDispoFlotte([]))
      .finally(() => setLoadingDispo(false));
  }, [remplacement.enabled, remplacement.duree_jours, form.date, form.heure_debut, remplacement.reservation_id]); // eslint-disable-line

  const fetchSuggestion = async (flotte_id) => {
    try {
      const period = getResaPeriod();
      const { data } = await api.get(`/reservations/flotte/${flotte_id}/prochaine-dispo`, {
        params: { date_debut: period.date_debut, duree_jours: remplacement.duree_jours },
      });
      setConflitFlotte({ flotte_id, suggestion: data });
    } catch (e) { setConflitFlotte(null); }
  };

  const canForce = user?.role === "admin" || user?.role === "comptoir";
  const [mecanos, setMecanos] = useState([]);
  useEffect(() => {
    if (!canForce) return;
    api.get("/atelier/mecaniciens")
      .then(({ data }) => setMecanos(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setMecanos([]));
  }, [canForce]);

  const submit = async ({ avecConfirmationSms = false } = {}) => {
    if (!form.client?.id || !form.type_intervention_id || !form.date || !form.heure_debut) {
      toast.error("Client, type d'intervention, date et heure sont requis");
      return;
    }
    setSubmitting(true);
    let rdvId = editing?.id || null;
    try {
      const payload = {
        client_id: form.client.id,
        vehicule_id: form.vehicule_id || null,
        type_intervention_id: form.type_intervention_id,
        date: form.date,
        heure_debut: form.heure_debut,
        heures_travail: form.heures_travail !== "" ? parseFloat(form.heures_travail) : null,
        jours_immobilisation: form.jours_immobilisation !== "" ? parseInt(form.jours_immobilisation, 10) : null,
        notes: form.notes || null,
        urgence: form.urgence,
        mecanicien_id: form.mecanicien_id || null,
        source: "interne",
      };
      if (editing) {
        await api.put(`/rdv/${editing.id}`, payload);
        // Gestion réservation lors d'une édition
        if (remplacement.enabled && remplacement.flotte_id && !remplacement.reservation_id) {
          // Ajout d'une nouvelle résa
          const period = getResaPeriod();
          try {
            await api.post("/reservations", {
              flotte_id: remplacement.flotte_id,
              rdv_id: editing.id,
              client_id: form.client.id,
              date_debut: period.date_debut,
              date_fin: period.date_fin,
              commentaire: remplacement.commentaire || null,
            });
          } catch (e) {
            if (e.response?.status === 409) {
              toast.error(e.response.data.detail);
              setSubmitting(false);
              return;
            }
            throw e;
          }
        } else if (!remplacement.enabled && remplacement.reservation_id) {
          // Suppression résa
          await api.delete(`/reservations/${remplacement.reservation_id}`).catch(() => {});
        }
        toast.success("RDV mis à jour");
      } else {
        const { data: rdvCree } = await api.post("/rdv", payload);
        rdvId = rdvCree.id;
        // Si réservation activée, on la crée après le RDV
        if (remplacement.enabled && remplacement.flotte_id) {
          const period = getResaPeriod();
          try {
            await api.post("/reservations", {
              flotte_id: remplacement.flotte_id,
              rdv_id: rdvCree.id,
              client_id: form.client.id,
              date_debut: period.date_debut,
              date_fin: period.date_fin,
              commentaire: remplacement.commentaire || null,
            });
          } catch (e) {
            if (e.response?.status === 409) {
              toast.warning("RDV créé, mais la réservation a échoué : " + e.response.data.detail);
            } else {
              toast.warning("RDV créé, mais la réservation a échoué.");
            }
          }
        }
        toast.success("RDV créé");
      }
      onSaved && onSaved();
      onClose();
      // iter289 — enchaîne sur l'aperçu du SMS de confirmation
      if (avecConfirmationSms && rdvId) onDemandeConfirmation?.(rdvId);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(`Capacité saturée : ${detail}`);
      } else {
        toast.error(detail || "Erreur");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="rdv-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le RDV" : "Nouveau rendez-vous"}</DialogTitle>
          <DialogDescription>
            Le système vérifie automatiquement la capacité disponible
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Client *</Label>
            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-start min-w-0">
              <div className="flex-1 min-w-0">
                <ClientSearchPicker value={form.client} onChange={(c) => setForm({ ...form, client: c, vehicule_id: "" })} />
              </div>
              {form.client?.id && (
                <a
                  href={`/clients/${form.client.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap sm:mt-2"
                  title="Modifier le client (nouvel onglet)"
                  data-testid="rdv-edit-client"
                >
                  ✏️ Modifier
                </a>
              )}
            </div>
          </div>

          {form.client && (
            <div>
              <Label>Véhicule <span className="text-xs text-gray-400 font-normal">(optionnel)</span></Label>
              {vehicules.length > 0 ? (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-start min-w-0">
                  <VehiculePicker
                    vehicules={vehicules}
                    value={form.vehicule_id}
                    onChange={(v) => setForm({ ...form, vehicule_id: v })}
                    allowNone
                    noneLabel="Aucun véhicule spécifique"
                    testId="rdv-vehicule"
                    showDetails
                    onVehiculeUpdated={(v) => setVehicules((prev) => prev.map((x) => (x.id === v.id ? { ...x, ...v } : x)))}
                  />
                  {form.vehicule_id && (
                    <a
                      href={`/clients/${form.client.id}?vehicule=${form.vehicule_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                      title="Modifier le véhicule (nouvel onglet)"
                      data-testid="rdv-edit-vehicule"
                    >
                      ✏️
                    </a>
                  )}
                  <Button type="button" variant="outline" onClick={() => setQuickVehOpen(true)} data-testid="rdv-add-vehicule">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 p-3 border border-dashed border-gray-300 bg-gray-50 rounded-md">
                  <div className="text-xs text-gray-600">
                    Aucun véhicule enregistré pour ce client.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setQuickVehOpen(true)}
                    data-testid="rdv-add-vehicule-empty"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un véhicule
                  </Button>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Type d'intervention *</Label>
            {/* iter51 — Remplacement dropdown par 2 boutons radio (Mécanique / Carrosserie).
                Le détail va dans `notes` (zone texte). Plus simple et déterministe. */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {types.filter((t) => t.generique).sort((a, b) => a.categorie.localeCompare(b.categorie)).map((t) => {
                const active = form.type_intervention_id === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setForm({ ...form, type_intervention_id: t.id })}
                    data-testid={`rdv-type-${t.categorie}`}
                    className={`p-3 rounded-lg border-2 transition flex flex-col items-center gap-1 ${
                      active ? "border-[#52a675] bg-emerald-50" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.couleur }} />
                    <span className={`text-sm font-semibold ${active ? "text-[#2d5f3f]" : "text-gray-700"}`}>{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rdv-date" />
            </div>
            <div>
              <Label>Heure de début *</Label>
              <Input type="time" value={form.heure_debut} onChange={(e) => setForm({ ...form, heure_debut: e.target.value })} data-testid="rdv-heure" />
            </div>
          </div>

          {/* iter266 — Qui prend le RDV en charge : hérité par l'OR créé depuis ce RDV.
              Un mécano qui crée lui-même le RDV est attribué automatiquement (serveur). */}
          {canForce && (
            <div>
              <Label>Attribué à</Label>
              <select
                value={form.mecanicien_id || ""}
                onChange={(e) => setForm({ ...form, mecanicien_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-[#52a675] focus:ring-1 focus:ring-[#52a675]/40 focus:outline-none"
                data-testid="rdv-mecanicien-select"
              >
                <option value="">— Personne pour l&apos;instant —</option>
                {mecanos.map((m) => (
                  <option key={m.id} value={m.id}>{m.label || m.nom}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-0.5">L&apos;OR créé depuis ce RDV sera attribué à cette personne.</p>
            </div>
          )}

          {dispo && (
            <div className={`p-3 rounded-md border text-sm ${
              dispo.disponible
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-orange-50 border-orange-200 text-orange-800"
            }`} data-testid="rdv-dispo-banner">
              <div className="flex items-start gap-2">
                {dispo.disponible ? <Check className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="font-semibold">{dispo.disponible ? "Créneau disponible" : "Capacité saturée"}</div>
                  <div className="text-xs">{dispo.raison} ({dispo.poids_apres}/{dispo.max})</div>
                </div>
              </div>
              {!dispo.disponible && canForce && (
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.urgence}
                    onChange={(e) => setForm({ ...form, urgence: e.target.checked })}
                    data-testid="rdv-urgence-checkbox"
                  />
                  <span className="text-xs font-semibold">Forcer (mode urgence — ex. dépanneuse)</span>
                </label>
              )}
            </div>
          )}

          {/* iter51 — Description libre (remplace les sous-types dropdown) */}
          {selectedType && (
            <div>
              <Label className="text-xs uppercase tracking-wider mb-1 block">
                Description de l'intervention
              </Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder={
                  selectedType.categorie === "carrosserie"
                    ? "ex: bosse pare-choc arrière, rayure aile droite…"
                    : "ex: contrôle freins, vidange, distribution…"
                }
                data-testid="rdv-notes"
              />
            </div>
          )}

          {/* Heures de travail / Jours d'immobilisation */}
          {selectedType && (
            <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3" data-testid="rdv-durees-section">
              {/* iter51 — Heures de travail uniquement pour mécanique
                  (carrosserie : pas d'estimation horaire fiable, on raisonne en véhicules/semaine) */}
              {selectedType.categorie !== "carrosserie" && (
                <div>
                  <Label className="flex items-center gap-1">
                    Temps (heures)
                    <span className="text-[10px] text-gray-400 font-normal">(main d'œuvre)</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    value={form.heures_travail}
                    onChange={(e) => setForm({ ...form, heures_travail: e.target.value })}
                    placeholder="ex: 1"
                    data-testid="rdv-heures-travail"
                  />
                </div>
              )}
              <div className={selectedType.categorie === "carrosserie" ? "col-span-2" : ""}>
                <Label className="flex items-center gap-1">
                  Jours d'immobilisation
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      selectedType.categorie === "carrosserie"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-[#52a675]/15 text-[#3d8a5c]"
                    }`}
                  >
                    {selectedType.categorie === "carrosserie" ? "carrosserie (3j par défaut)" : "mécanique"}
                  </span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="60"
                  value={form.jours_immobilisation}
                  onChange={(e) => setForm({ ...form, jours_immobilisation: e.target.value })}
                  placeholder="0 = même jour"
                  data-testid="rdv-jours-immo"
                />
              </div>
              <div className="col-span-2 text-[11px] text-gray-500">
                <span className="font-semibold">Astuce :</span> {selectedType.categorie === "carrosserie"
                  ? "le compteur carrosserie raisonne en 5 véhicules max/semaine. Les jours d'immobilisation bloquent l'agenda et la réservation véhicule de remplacement."
                  : "les heures servent au planning de charge atelier (14h max/jour) ; les jours bloquent la place dans l'agenda et conditionnent la durée de réservation du véhicule de remplacement."}
              </div>
            </div>
          )}

          {/* Véhicule de remplacement */}
          {form.client && (
            <div className="border-t border-gray-100 pt-3" data-testid="rdv-remplacement-section">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remplacement.enabled}
                  onChange={(e) => setRemplacement({ ...remplacement, enabled: e.target.checked })}
                  className="w-4 h-4 accent-[#52a675]"
                  data-testid="rdv-remplacement-toggle"
                />
                <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                  <span>🚗</span> Véhicule de remplacement nécessaire
                </span>
              </label>

              {remplacement.enabled && remplacement.reservation_id && (
                <div className="mt-2 p-2.5 bg-[#f0f9f4] border border-[#52a675]/30 rounded text-sm flex items-center justify-between" data-testid="rdv-remplacement-existing">
                  <div>
                    <span className="text-xs text-gray-500">Réservation existante :</span>
                    <div className="font-semibold">{remplacement.label}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRemplacement({ ...remplacement, enabled: false })}
                    className="text-red-600 text-xs hover:underline"
                    data-testid="rdv-remplacement-remove"
                  >
                    Libérer
                  </button>
                </div>
              )}

              {remplacement.enabled && !remplacement.reservation_id && (
                <div className="mt-3 space-y-3 pl-6 border-l-2 border-[#52a675]/20">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Durée (jours)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={remplacement.duree_jours}
                        onChange={(e) => setRemplacement({ ...remplacement, duree_jours: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                        data-testid="rdv-remplacement-duree"
                      />
                    </div>
                    <div className="text-xs text-gray-500 flex items-end pb-2">
                      Du {form.date} au{" "}
                      {new Date(new Date(`${form.date}T${form.heure_debut}:00`).getTime() + remplacement.duree_jours * 86400000).toLocaleDateString("fr-CH")}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Véhicule disponible</Label>
                    {loadingDispo ? (
                      <div className="py-2 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Recherche…</div>
                    ) : dispoFlotte.length === 0 ? (
                      <div className="bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-900" data-testid="rdv-remplacement-empty">
                        <div className="font-semibold mb-1">⚠ Aucun véhicule disponible sur cette période.</div>
                        {allFlotte.filter((f) => f.actif && f.statut !== "maintenance").map((f) => (
                          <button
                            type="button"
                            key={f.id}
                            onClick={() => fetchSuggestion(f.id)}
                            className="block w-full text-left hover:underline mt-1"
                          >
                            → Voir prochaine dispo pour {f.plaque} · {f.marque} {f.modele}
                          </button>
                        ))}
                        {conflitFlotte?.suggestion && (
                          <div className="mt-2 p-2 bg-white rounded border border-orange-300">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-orange-700">Suggestion</div>
                            <div className="text-xs">
                              Dispo dès le {conflitFlotte.suggestion.date_debut_suggeree.slice(0, 16).replace("T", " à ")}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Select value={remplacement.flotte_id} onValueChange={(v) => setRemplacement({ ...remplacement, flotte_id: v })}>
                        <SelectTrigger data-testid="rdv-remplacement-select"><SelectValue placeholder="Sélectionner un véhicule…" /></SelectTrigger>
                        <SelectContent>
                          {dispoFlotte.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.marque} {f.modele} · {f.plaque}{f.couleur ? ` (${f.couleur})` : ""} — {(f.tarif_jour || 0).toFixed(0)} CHF/j
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs">Commentaire (optionnel)</Label>
                    <Input
                      value={remplacement.commentaire}
                      onChange={(e) => setRemplacement({ ...remplacement, commentaire: e.target.value })}
                      placeholder="Ex: Boîte auto, sièges enfants…"
                      data-testid="rdv-remplacement-commentaire"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={() => submit()}
            disabled={submitting || (!editing && dispo && !dispo.disponible && !form.urgence)}
            variant="outline"
            className="border-[#52a675] text-[#2d5f3f] hover:bg-[#f0f9f4]"
            data-testid="rdv-save"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Mettre à jour" : "Créer le RDV")}
          </Button>
          {/* iter289 — créer (ou mettre à jour) puis envoyer la confirmation au client */}
          <Button
            onClick={() => submit({ avecConfirmationSms: true })}
            disabled={submitting || (!editing && dispo && !dispo.disponible && !form.urgence)}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            title="Enregistre le rendez-vous puis propose le SMS de confirmation au client"
            data-testid="rdv-save-with-sms"
          >
            {submitting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><MessageSquareText className="w-4 h-4 mr-2" /> {editing ? "Enregistrer + SMS" : "Créer + SMS de confirmation"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
      <QuickVehiculeDialog
        open={quickVehOpen}
        onClose={() => setQuickVehOpen(false)}
        clientId={form.client?.id}
        clientNom={form.client?.nom}
        onCreated={(v) => {
          setVehicules((prev) => [...prev, v]);
          setForm((f) => ({ ...f, vehicule_id: v.id }));
        }}
      />
    </Dialog>
  );
}

export default function CalendrierPage() {
  const navigate = useNavigate();
  const [weekDate, setWeekDate] = useState(toIsoDate(new Date()));
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(null);
  const [editing, setEditing] = useState(null);
  const [detailRdv, setDetailRdv] = useState(null);
  // iter115 Session 1 — Modal preview SMS no-show
  const [relancePreview, setRelancePreview] = useState({ open: false, rdv: null, preview: null });
  // iter289 — SMS de confirmation de rendez-vous (aperçu modifiable avant envoi)
  const [confirmSms, setConfirmSms] = useState({ open: false, rdvId: null, preview: null, message: "" });
  const [confirmSending, setConfirmSending] = useState(false);

  const ouvrirConfirmationSms = async (rdvId) => {
    try {
      const { data } = await api.get(`/rdv/${rdvId}/confirmation-sms/preview`);
      setConfirmSms({ open: true, rdvId, preview: data, message: data.message || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aperçu du SMS impossible");
    }
  };

  const envoyerConfirmationSms = async () => {
    if (!confirmSms.rdvId) return;
    setConfirmSending(true);
    try {
      const { data } = await api.post(`/rdv/${confirmSms.rdvId}/confirmation-sms`, {
        message: confirmSms.message,
      });
      toast.success(`Confirmation envoyée à ${data.to} (${data.sms_status || "ok"})`);
      setConfirmSms({ open: false, rdvId: null, preview: null, message: "" });
      await fetchWeek();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setConfirmSending(false);
    }
  };
  const [relanceLoading, setRelanceLoading] = useState(false);
  // iter116 Session 2 — Modal Prise en charge (mobile QR / sur place)
  const [pecModal, setPecModal] = useState({ open: false, rdvId: null });
  // iter118 Session 4 — Modal "Démarrer l'OR" (depuis une PEC complète)
  const [demarrerOrModal, setDemarrerOrModal] = useState({ open: false, token: null });
  // Liste des sessions PEC du RDV ouvert (active/complete) pour afficher "Démarrer l'OR" si applicable
  const [pecSessions, setPecSessions] = useState([]);
  const [blocageDialog, setBlocageDialog] = useState({ open: false, date: null, existing: [] });

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      // iter67 — On démarre toujours sur "aujourd'hui" et on étend à 5 jours ouvrés
      const { data } = await api.get("/calendrier/semaine", { params: { date: weekDate, start_mode: "today" } });
      setWeek(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [weekDate]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  const navigateWeek = (delta) => {
    // iter67 — Avance/recule de 5 jours ouvrés à la fois
    const d = new Date(weekDate + "T00:00:00");
    let step = 0;
    let added = 0;
    const direction = delta > 0 ? 1 : -1;
    const target = Math.abs(delta) * 5;
    while (added < target) {
      d.setDate(d.getDate() + direction);
      step++;
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
      if (step > 30) break;
    }
    setWeekDate(toIsoDate(d));
  };

  const resetToToday = () => setWeekDate(toIsoDate(new Date()));

  const openNewRdv = (date) => {
    setDialogDate(date);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEditRdv = (rdv) => {
    setEditing(rdv);
    setDialogDate(rdv.date);
    setDialogOpen(true);
    setDetailRdv(null);
  };

  const changeStatut = async (rid, statut) => {
    try {
      await api.patch(`/rdv/${rid}/statut`, { statut });
      toast.success(`RDV ${STATUT_LABELS[statut].toLowerCase()}`);
      fetchWeek();
      setDetailRdv(null);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const deleteRdv = async (rid) => {
    if (!window.confirm("Supprimer définitivement ce RDV ?")) return;
    try {
      await api.delete(`/rdv/${rid}`);
      toast.success("RDV supprimé");
      fetchWeek();
      setDetailRdv(null);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const carro = week?.carrosserie_semaine;
  const today = toIsoDate(new Date());

  // iter326 — Arrivée depuis le raccourci « Rendez-vous » du tableau de bord
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("nouveau") !== "1") return;
    openNewRdv(toIsoDate(new Date()));
    setParams({}, { replace: true });
  }, [params, setParams]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="calendrier-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-7 h-7 text-[#52a675]" /> Calendrier
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Capacité atelier · gestion des rendez-vous
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="cal-prev-week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekDate(toIsoDate(new Date()))} data-testid="cal-today">
            Aujourd'hui
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="cal-next-week">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button onClick={() => openNewRdv(today)} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="cal-new-rdv">
            <Plus className="w-4 h-4 mr-2" /> Nouveau RDV
          </Button>
        </div>
      </div>

      {/* Carrosserie counter */}
      {week && carro && (
        <Card className="p-4 mb-4 border-pink-200 bg-gradient-to-r from-pink-50/50 to-transparent">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-pink-100 text-pink-700 flex items-center justify-center">
                <Car className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm text-gray-900">Carrosserie · semaine du {fmtDate(week.lundi)}</div>
                <div className="text-xs text-gray-600">
                  Dépose lun-mar-mer · Retour vendredi · Quota hebdo
                </div>
              </div>
            </div>
            <div className="flex-1 max-w-md">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs text-gray-500">{carro.consomme} / {carro.max} véhicules</span>
                <span className={`text-xs font-semibold ${carro.consomme >= carro.max ? "text-red-600" : "text-pink-700"}`}>
                  {Math.round((carro.consomme / carro.max) * 100)}%
                </span>
              </div>
              <Progress value={(carro.consomme / carro.max) * 100} className="h-2" />
            </div>
          </div>
        </Card>
      )}

      {loading && <div className="text-center text-gray-400 py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {/* Légende types d'intervention */}
      <div className="flex items-center gap-4 mb-3 text-xs text-gray-600 flex-wrap" data-testid="calendrier-legende">
        <span className="font-semibold text-gray-700">Légende :</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-[#52a675]"></span> Mécanique
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-blue-600"></span> Carrosserie
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gradient-to-r from-blue-600 to-[#52a675]"></span> Mixte
        </span>
        <span className="inline-flex items-center gap-1.5 ml-3">
          <span className="w-3 h-3 rounded border border-dashed border-gray-400" style={{ background: "repeating-linear-gradient(45deg, #f9fafb 0 4px, white 4px 8px)" }}></span> Immobilisation
        </span>
      </div>

      {/* Week grid */}
      {!loading && week && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
          {week.jours.map((j) => {
            const pct = j.capacite_max > 0 ? (j.poids_consomme / j.capacite_max) * 100 : 0;
            const isToday = j.date === today;
            return (
              <Card
                key={j.date}
                className={`flex flex-col border-gray-200 ${isToday ? "ring-2 ring-[#52a675]" : ""} ${!j.ouvert ? "opacity-60 bg-gray-50" : ""}`}
                data-testid={`day-${j.date}`}
              >
                <div className="p-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                        {JOURS_LABELS[j.jour_semaine].slice(0, 3)}
                      </div>
                      <div className="text-lg font-heading font-bold text-gray-900">
                        {fmtDate(j.date, { day: "2-digit", month: "short" })}
                      </div>
                    </div>
                    {j.ouvert && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setBlocageDialog({ open: true, date: j.date, existing: j.blocages || [] })}
                          className={`w-7 h-7 rounded-md flex items-center justify-center ${(j.blocages || []).length > 0 ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-gray-100 text-gray-500 hover:bg-amber-100 hover:text-amber-700"}`}
                          title={(j.blocages || []).length > 0 ? `Blocages : ${j.blocages.map((b) => b.type).join(", ")}` : "Gérer les blocages"}
                          data-testid={`day-${j.date}-blocage`}
                        >
                          <Lock className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openNewRdv(j.date)}
                          className="w-7 h-7 rounded-md bg-[#52a675]/10 hover:bg-[#52a675]/20 text-[#52a675] flex items-center justify-center"
                          title="Nouveau RDV"
                          data-testid={`day-${j.date}-add`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Badges blocages actifs */}
                  {(j.blocages || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`day-${j.date}-blocages-badges`}>
                      {j.blocages.map((b) => (
                        <Badge key={b.id} variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] font-semibold">
                          🚫 {BLOCAGE_LABELS[b.type] || b.type}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {j.ouvert && (
                    <div className="mt-2">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-[10px] text-gray-500">{j.poids_consomme}/{j.capacite_max} places</span>
                        <span className={`text-[10px] font-bold ${pct >= 100 ? "text-red-600" : pct >= 75 ? "text-orange-600" : "text-[#52a675]"}`}>
                          {Math.round(pct)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-orange-500" : "bg-[#52a675]"}`}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {!j.ouvert && (
                    <div className="mt-2 text-[10px] text-gray-400" data-testid={`day-${j.date}-closed-reason`}>
                      {j.closed_reason || "Fermé"}
                    </div>
                  )}
                </div>
                <div className="flex-1 p-2 space-y-1.5 min-h-[120px]">
                  {j.rdvs.length === 0 && j.ouvert && (
                    <button
                      onClick={() => openNewRdv(j.date)}
                      className="w-full text-xs text-gray-300 hover:text-[#52a675] py-3"
                    >
                      + Ajouter
                    </button>
                  )}
                  {j.rdvs.map((r) => {
                    const isExt = r.is_extension;
                    const catColor = r.type_categorie === "carrosserie" ? "#2563eb" : (r.type_categorie === "mixte" ? "linear-gradient(180deg, #2563eb 50%, #52a675 50%)" : "#52a675");
                    const tooltip = `${r.type_label}\nHeures travail: ${r.heures_travail ?? "—"}h\nImmobilisation: ${r.jours_immobilisation || 0}j${r.reservation_label ? "\n🚗 " + r.reservation_label : ""}`;
                    // iter69 — RDV en retard : date+heure passées, pas d'OR créé, pas annulé/réalisé
                    const isLate = (() => {
                      if (isExt || r.or_id) return false;
                      if (r.statut === "annule" || r.statut === "realise" || r.statut === "no_show") return false;
                      try {
                        const rdvDt = new Date(`${r.date}T${r.heure_debut || "00:00"}:00`);
                        return rdvDt.getTime() < Date.now();
                      } catch {
                        return false;
                      }
                    })();
                    return (
                      <button
                        key={`${r.id}-${j.date}`}
                        onClick={async () => {
                          setDetailRdv(r);
                          // iter118 — récupère les sessions PEC pour décider du bouton (Prise en charge vs Démarrer l'OR)
                          try {
                            const { data } = await api.get(`/rdv/${r.id}/prise-en-charge/sessions`);
                            setPecSessions(data.sessions || []);
                          } catch {
                            setPecSessions([]);
                          }
                        }}
                        className={`w-full text-left p-1.5 rounded border-l-4 transition-shadow text-xs ${
                          isExt
                            ? "bg-gray-50/80 border-dashed border opacity-80"
                            : isLate
                              ? "bg-red-50 border border-red-300 hover:shadow-sm animate-pulse"
                              : "bg-white hover:shadow-sm"
                        }`}
                        style={{
                          borderLeftColor: isLate ? "#dc2626" : (r.type_categorie === "carrosserie" ? "#2563eb" : r.type_couleur),
                          background: isExt
                            ? "repeating-linear-gradient(45deg, #f9fafb 0 6px, white 6px 12px)"
                            : undefined,
                        }}
                        title={isLate ? `⚠ EN RETARD — Appeler le client\n\n${tooltip}` : tooltip}
                        data-testid={`rdv-${r.id}${isExt ? "-ext" : ""}${isLate ? "-late" : ""}`}
                      >
                        <div className="flex items-center gap-1">
                          {isExt ? (
                            <span className="text-[9px] uppercase tracking-wider font-bold text-gray-400">Immo</span>
                          ) : (
                            <>
                              <Clock className={`w-3 h-3 flex-shrink-0 ${isLate ? "text-red-600" : "text-gray-400"}`} />
                              <span className={`font-bold ${isLate ? "text-red-700" : ""}`}>{r.heure_debut}</span>
                              {r.heures_travail > 0 && <span className="text-[10px] text-gray-500">· {r.heures_travail}h</span>}
                              {r.or_id && (
                                <Badge
                                  variant="outline"
                                  className="bg-blue-50 text-blue-700 border-blue-200 text-[8px] px-1 py-0 font-bold cursor-pointer hover:bg-blue-100"
                                  data-testid={`rdv-${r.id}-or-badge`}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/atelier/or/${r.or_id}`); }}
                                  role="link"
                                  title={`Ouvrir OR ${r.or_numero || ""}`}
                                >
                                  OR {r.or_numero || ""}
                                </Badge>
                              )}
                              {r.devis_id && (
                                <Badge
                                  variant="outline"
                                  className="bg-purple-50 text-purple-700 border-purple-200 text-[8px] px-1 py-0 font-bold cursor-pointer hover:bg-purple-100"
                                  data-testid={`rdv-${r.id}-devis-badge`}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/devis/${r.devis_id}`); }}
                                  role="link"
                                  title={`Ouvrir Devis ${r.devis_numero || ""}`}
                                >
                                  Devis {r.devis_numero || ""}
                                </Badge>
                              )}
                              {isLate && (
                                <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[8px] px-1 py-0 font-bold ml-auto" data-testid={`rdv-${r.id}-late-badge`}>
                                  ⚠ RETARD
                                </Badge>
                              )}
                              {r.urgence && !isLate && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[8px] px-1 py-0">URG</Badge>}
                            </>
                          )}
                        </div>
                        <div className={`font-semibold truncate mt-0.5 ${isExt ? "text-gray-500" : "text-gray-900"}`}>{r.client_nom}</div>
                        <div className="text-gray-500 truncate flex items-center gap-1">
                          {r.type_categorie === "carrosserie" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600" />}
                          {r.type_categorie === "mecanique" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#52a675]" />}
                          {r.type_categorie === "mixte" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-[#52a675]" />}
                          {r.type_label}
                          {!isExt && r.jours_immobilisation > 0 && <span className="text-[9px] text-gray-400 ml-auto">· {r.jours_immobilisation}j immo</span>}
                        </div>
                        {!isExt && r.reservation_label && (
                          <div className="text-[10px] mt-0.5 px-1.5 py-0.5 bg-[#52a675]/10 text-[#2d5f3f] rounded inline-flex items-center gap-0.5 font-semibold" title={`Véhicule de remplacement : ${r.reservation_label}`} data-testid={`rdv-${r.id}-resa`}>
                            🚗 {r.reservation_label.split('·')[1]?.trim() || r.reservation_label}
                          </div>
                        )}
                        {!isExt && r.statut !== "planifie" && (
                          <Badge variant="outline" className={`text-[8px] mt-1 ${STATUT_BADGE[r.statut]}`}>
                            {STATUT_LABELS[r.statut]}
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* RDV dialog */}
      <RdvDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        defaultDate={dialogDate}
        editing={editing}
        onSaved={fetchWeek}
        onDemandeConfirmation={ouvrirConfirmationSms}
      />

      {/* RDV detail dialog */}
      <Dialog open={!!detailRdv} onOpenChange={(o) => !o && setDetailRdv(null)}>
        <DialogContent data-testid="rdv-detail-dialog">
          {detailRdv && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: detailRdv.type_couleur }} />
                  <DialogTitle>{detailRdv.type_label}</DialogTitle>
                </div>
                <DialogDescription>
                  {fmtDate(detailRdv.date, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} à {detailRdv.heure_debut}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/clients/${detailRdv.client_id}`)}
                  className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-md flex items-center gap-2"
                  data-testid="rdv-detail-client-link"
                >
                  <UserIcon className="w-4 h-4 text-[#52a675]" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{detailRdv.client_nom}</div>
                    {detailRdv.vehicule_label && (
                      <div className="text-xs text-gray-500 mt-0.5">{detailRdv.vehicule_label}</div>
                    )}
                  </div>
                </button>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="outline" className={STATUT_BADGE[detailRdv.statut]}>
                    {STATUT_LABELS[detailRdv.statut]}
                  </Badge>
                  <Badge variant="outline" className={detailRdv.type_categorie === "carrosserie" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-[#52a675]/15 text-[#3d8a5c]"}>
                    {detailRdv.type_categorie || "mécanique"}
                  </Badge>
                  {detailRdv.heures_travail > 0 && (
                    <Badge variant="outline" className="bg-gray-50">⏱ {detailRdv.heures_travail}h travail</Badge>
                  )}
                  {detailRdv.jours_immobilisation > 0 && (
                    <Badge variant="outline" className="bg-gray-50">📅 {detailRdv.jours_immobilisation}j immobilisé</Badge>
                  )}
                  {detailRdv.urgence && (
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">URGENCE</Badge>
                  )}
                </div>
                {detailRdv.notes && (
                  <div className="p-3 bg-yellow-50 border border-yellow-100 rounded-md text-sm">
                    {detailRdv.notes}
                  </div>
                )}

                {/* iter289/291 — traces des SMS envoyés au client pour ce rendez-vous */}
                {(detailRdv.confirmation_sms_at || detailRdv.rappel_sms_at) && (
                  <div className="text-xs text-gray-600 space-y-0.5" data-testid="rdv-sms-traces">
                    {detailRdv.confirmation_sms_at && (
                      <div>Confirmation envoyée le {fmtHorodatage(detailRdv.confirmation_sms_at)}</div>
                    )}
                    {detailRdv.rappel_sms_at && (
                      <div>Rappel de la veille envoyé le {fmtHorodatage(detailRdv.rappel_sms_at)}</div>
                    )}
                  </div>
                )}

                {/* iter67 — Édition rapide du temps estimé par l'IA */}
                {!detailRdv.or_id && (
                  <div className="border-t pt-3">
                    <Label className="text-xs">Temps estimé (heures) — modifiable</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="16"
                        defaultValue={detailRdv.heures_estimees ?? detailRdv.heures_travail ?? ""}
                        onBlur={async (e) => {
                          const v = parseFloat(e.target.value);
                          if (isNaN(v) || v === (detailRdv.heures_estimees ?? detailRdv.heures_travail)) return;
                          try {
                            await api.patch(`/rdv/${detailRdv.id}`, { heures_estimees: v });
                            toast.success(`Temps mis à jour : ${v}h`);
                            await fetchWeek();
                            setDetailRdv({ ...detailRdv, heures_estimees: v });
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Erreur");
                          }
                        }}
                        className="w-24"
                        data-testid="rdv-heures-estimees-input"
                      />
                      <div className="flex gap-1">
                        {[1, 2, 4, 8].map((h) => (
                          <Button
                            key={h}
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={async () => {
                              try {
                                await api.patch(`/rdv/${detailRdv.id}`, { heures_estimees: h });
                                toast.success(`Temps : ${h}h`);
                                await fetchWeek();
                                setDetailRdv({ ...detailRdv, heures_estimees: h });
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Erreur");
                              }
                            }}
                            data-testid={`rdv-heures-preset-${h}`}
                          >
                            {h}h
                          </Button>
                        ))}
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Modifie l'estimation de l'IA. La capacité atelier est recalculée immédiatement.
                    </p>
                  </div>
                )}

                {detailRdv.or_id && (
                  <div className="border-t pt-3 bg-blue-50/50 -mx-6 px-6 py-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-blue-600 text-white">OR créé · {detailRdv.or_numero}</Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/atelier/or/${detailRdv.or_id}`)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="rdv-detail-open-or"
                    >
                      → Ouvrir l'OR
                    </Button>
                  </div>
                )}

                {/* iter69 — Statuts manuels cachés si OR créé (statut piloté par l'OR).
                    iter124 — Bloc « Changer le statut » retiré (les 4 boutons étaient peu utilisés).
                    Le statut évolue automatiquement via Prise en charge / OR / SMS no-show. */}
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => deleteRdv(detailRdv.id)}
                  className="text-red-600 hover:text-red-700 sm:mr-auto"
                  data-testid="rdv-detail-delete"
                >
                  Supprimer
                </Button>
                <Button variant="outline" onClick={() => openEditRdv(detailRdv)} data-testid="rdv-detail-edit">
                  Modifier
                </Button>
                {!detailRdv.or_id && (() => {
                  const completeSession = pecSessions.find((s) => s.statut === "complete" && s.ready_for_or && !s.or_id);
                  return (
                    <>
                      <Button
                        onClick={async () => {
                          // iter115 Session 1 — Bouton "Relancer le client" : preview SMS no-show
                          try {
                            const { data } = await api.get(`/rdv/${detailRdv.id}/relance-no-show/preview`);
                            setRelancePreview({
                              open: true, rdv: detailRdv,
                              preview: { ...data, message_origine: data.message },
                            });
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Erreur");
                          }
                        }}
                        variant="outline"
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        data-testid="rdv-detail-relance-noshow"
                      >
                        📩 Relancer le client
                      </Button>
                      {completeSession ? (
                        <Button
                          onClick={() => {
                            const tk = completeSession.token;
                            setDetailRdv(null);
                            setTimeout(() => setDemarrerOrModal({ open: true, token: tk }), 50);
                          }}
                          className="bg-[#52a675] hover:bg-[#3d8a5c]"
                          data-testid="rdv-detail-demarrer-or"
                        >
                          🚀 Démarrer l'OR
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            // iter116 Session 2 — Ouvre la modale de choix Mobile/SurPlace + QR
                            const id = detailRdv.id;
                            setDetailRdv(null);  // ferme le Dialog Radix pour éviter l'interception pointer
                            setTimeout(() => setPecModal({ open: true, rdvId: id }), 50);
                          }}
                          className="bg-[#52a675] hover:bg-[#3d8a5c]"
                          data-testid="rdv-detail-prise-en-charge"
                        >
                          🚗 Prise en charge
                        </Button>
                      )}
                    </>
                  );
                })()}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog blocage manuel de capacité (Feb 2026) */}
      <BlocageDialog
        open={blocageDialog.open}
        date={blocageDialog.date}
        existing={blocageDialog.existing}
        onClose={() => setBlocageDialog({ open: false, date: null, existing: [] })}
        onChanged={() => { fetchWeek(); setBlocageDialog({ open: false, date: null, existing: [] }); }}
      />

      {/* iter116 Session 2 — Modal Prise en charge (Mobile QR / Sur place) */}
      <PriseEnChargeModal
        rdvId={pecModal.rdvId}
        open={pecModal.open}
        onClose={() => setPecModal({ open: false, rdvId: null })}
        onSurPlace={(data) => {
          // iter117 Session 3 — naviguer vers la page desktop sur place
          navigate(`/prise-en-charge-sur-place/${data.token}`);
        }}
      />

      {/* iter118 Session 4 — Modal Démarrer l'OR (depuis une PEC complète) */}
      <DemarrerOrModal
        open={demarrerOrModal.open}
        token={demarrerOrModal.token}
        onClose={() => { setDemarrerOrModal({ open: false, token: null }); fetchWeek(); }}
      />

      {/* iter115 Session 1 — Modal preview SMS no-show (avant envoi + confirmation) */}
      <Dialog open={relancePreview.open} onOpenChange={(o) => !o && setRelancePreview({ open: false, rdv: null, preview: null })}>
        <DialogContent className="max-w-md" data-testid="rdv-relance-modal">
          <DialogHeader>
            <DialogTitle>📩 Relancer le client (no-show)</DialogTitle>
            <DialogDescription>
              Vérifie le message avant envoi. Le RDV sera marqué comme <b>non honoré</b>.
            </DialogDescription>
          </DialogHeader>
          {relancePreview.preview && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="text-gray-600">À :</div>
                <div className="font-semibold">{relancePreview.preview.client_nom}</div>
                <div className="font-mono text-xs">{relancePreview.preview.to}</div>
              </div>
              <div className="text-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-gray-600">Message SMS (modifiable) :</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-400">
                      {(relancePreview.preview.message || "").length} car.
                    </span>
                    <button
                      type="button"
                      className="text-[11px] text-gray-500 hover:text-gray-800 underline"
                      onClick={() => setRelancePreview((p) => ({
                        ...p, preview: { ...p.preview, message: p.preview.message_origine ?? p.preview.message },
                      }))}
                      data-testid="rdv-relance-reset-text"
                    >
                      Texte par défaut
                    </button>
                  </div>
                </div>
                <Textarea
                  value={relancePreview.preview.message || ""}
                  onChange={(e) => setRelancePreview((p) => ({
                    ...p, preview: { ...p.preview, message: e.target.value },
                  }))}
                  rows={10}
                  maxLength={800}
                  className="bg-gray-50 text-xs font-mono leading-relaxed resize-none"
                  data-testid="rdv-relance-preview-text"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setRelancePreview({ open: false, rdv: null, preview: null })}
              disabled={relanceLoading}
              data-testid="rdv-relance-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={async () => {
                if (!relancePreview.rdv) return;
                setRelanceLoading(true);
                try {
                  const { data } = await api.post(`/rdv/${relancePreview.rdv.id}/relance-no-show`, {
                    message: relancePreview.preview?.message || "",
                  });
                  toast.success(`SMS envoyé · RDV no-show (${data.sms_status || "ok"})`);
                  setRelancePreview({ open: false, rdv: null, preview: null });
                  setDetailRdv(null);
                  await fetchWeek();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erreur envoi SMS");
                } finally {
                  setRelanceLoading(false);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={relanceLoading || !relancePreview.preview}
              data-testid="rdv-relance-confirm"
            >
              {relanceLoading ? "Envoi…" : "Envoyer SMS + Marquer no-show"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter289 — SMS de confirmation de rendez-vous (aperçu modifiable) */}
      <Dialog
        open={confirmSms.open}
        onOpenChange={(o) => !o && !confirmSending && setConfirmSms({ open: false, rdvId: null, preview: null, message: "" })}
      >
        <DialogContent className="max-w-md" data-testid="rdv-confirmation-sms-modal">
          <DialogHeader>
            <DialogTitle>Confirmer le rendez-vous par SMS</DialogTitle>
            <DialogDescription>
              Vérifiez (et modifiez si besoin) le message, puis envoyez-le au client.
            </DialogDescription>
          </DialogHeader>
          {confirmSms.preview && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="text-gray-600">À :</div>
                <div className="font-semibold">{confirmSms.preview.client_nom}</div>
                <div className="font-mono text-xs">{confirmSms.preview.to}</div>
              </div>
              {confirmSms.preview.deja_envoye_at && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="rdv-confirmation-deja-envoye">
                  Une confirmation a déjà été envoyée pour ce rendez-vous.
                </div>
              )}
              <div className="text-sm">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-gray-600">Message SMS (modifiable) :</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-400">{confirmSms.message.length} car.</span>
                    <button
                      type="button"
                      className="text-[11px] text-gray-500 hover:text-gray-800 underline"
                      onClick={() => setConfirmSms((c) => ({ ...c, message: c.preview.message }))}
                      data-testid="rdv-confirmation-reset-text"
                    >
                      Texte par défaut
                    </button>
                  </div>
                </div>
                <Textarea
                  value={confirmSms.message}
                  onChange={(e) => setConfirmSms((c) => ({ ...c, message: e.target.value }))}
                  rows={10}
                  maxLength={800}
                  className="bg-gray-50 text-xs font-mono leading-relaxed resize-none"
                  data-testid="rdv-confirmation-text"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmSms({ open: false, rdvId: null, preview: null, message: "" })}
              disabled={confirmSending}
              data-testid="rdv-confirmation-cancel"
            >
              Ne pas envoyer
            </Button>
            <Button
              onClick={envoyerConfirmationSms}
              disabled={confirmSending || !confirmSms.message.trim()}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="rdv-confirmation-send"
            >
              {confirmSending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><MessageSquareText className="w-4 h-4 mr-2" /> Envoyer la confirmation</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlocageDialog({ open, date, existing, onClose, onChanged }) {
  const [type, setType] = useState("total");
  const [raison, setRaison] = useState("");
  const [saving, setSaving] = useState(false);
  // iter52 — Mode "semaine" pour blocage carrosserie (5 véhicules/semaine)
  const [scope, setScope] = useState("jour"); // 'jour' | 'semaine'

  useEffect(() => {
    if (open) { setType("total"); setRaison(""); setScope("jour"); }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (scope === "semaine") {
        await api.post("/capacite/blocages/semaine", { lundi: date, type, raison: raison.trim() || null });
        toast.success(`Semaine bloquée (${type})`);
      } else {
        await api.post("/capacite/blocages", { date, type, raison: raison.trim() || null });
        toast.success(`Blocage ${type} appliqué`);
      }
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (bid) => {
    if (!window.confirm("Supprimer ce blocage ?")) return;
    setSaving(true);
    try {
      await api.delete(`/capacite/blocages/${bid}`);
      toast.success("Blocage retiré");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const removeWeek = async (groupeId) => {
    if (!window.confirm("Supprimer le blocage de toute la semaine ?")) return;
    setSaving(true);
    try {
      await api.delete(`/capacite/blocages/semaine/${groupeId}`);
      toast.success("Semaine débloquée");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="blocage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4 text-red-600" /> Bloquer la capacité atelier</DialogTitle>
          <DialogDescription>
            Bloque manuellement la prise de RDV pour cette journée. Les créneaux libres restent visibles mais aucun nouveau RDV ne pourra être pris pour le type bloqué.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm font-mono text-gray-700 mb-2">{date}</div>

        {existing.length > 0 && (
          <div className="mb-3 space-y-1.5">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Blocages actifs</div>
            {existing.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded px-2 py-1.5 text-xs">
                <div>
                  <span className="font-semibold text-red-700">🚫 {BLOCAGE_LABELS[b.type] || b.type}</span>
                  {b.groupe_semaine_id && (
                    <span className="ml-1.5 text-[9px] font-bold bg-blue-100 text-blue-700 px-1 py-0.5 rounded">SEMAINE</span>
                  )}
                  {b.raison && <span className="text-gray-600 ml-2">— {b.raison}</span>}
                  <div className="text-[10px] text-gray-400 mt-0.5">par {b.created_by_nom}</div>
                </div>
                {b.groupe_semaine_id ? (
                  <button type="button" onClick={() => removeWeek(b.groupe_semaine_id)} disabled={saving}
                    className="text-red-600 hover:bg-red-100 p-1 rounded disabled:opacity-50"
                    title="Supprimer toute la semaine"
                    data-testid={`blocage-remove-week-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button type="button" onClick={() => remove(b.id)} disabled={saving}
                    className="text-red-600 hover:bg-red-100 p-1 rounded disabled:opacity-50"
                    data-testid={`blocage-remove-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          {/* iter52 — Choix portée du blocage : Jour (toute catégorie) ou Semaine (carrosserie surtout) */}
          <div>
            <Label className="text-xs">Portée du blocage *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                onClick={() => setScope("jour")}
                data-testid="blocage-scope-jour"
                className={`p-2 rounded-md border-2 text-xs font-semibold transition ${
                  scope === "jour" ? "border-red-500 bg-red-50 text-red-700" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                📅 1 jour
              </button>
              <button
                type="button"
                onClick={() => { setScope("semaine"); setType("carrosserie"); }}
                data-testid="blocage-scope-semaine"
                className={`p-2 rounded-md border-2 text-xs font-semibold transition ${
                  scope === "semaine" ? "border-red-500 bg-red-50 text-red-700" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                📆 1 semaine (lun-dim)
              </button>
            </div>
            {scope === "semaine" && (
              <p className="text-[11px] text-gray-500 mt-1.5">
                Recommandé pour <strong>carrosserie</strong> (capacité = 5 véhicules/semaine).
                Le lundi de la semaine de cette date sera la référence.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Type de blocage *</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="blocage-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="total">🚫 Journée bloquée (tout)</SelectItem>
                <SelectItem value="mecanique">🔧 Mécanique uniquement</SelectItem>
                <SelectItem value="carrosserie">🎨 Carrosserie uniquement</SelectItem>
                <SelectItem value="rapide">⚡ Service rapide</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Raison (optionnel)</Label>
            <Input
              value={raison}
              onChange={(e) => setRaison(e.target.value)}
              placeholder="Ex: Manque de personnel, ponts saturés…"
              data-testid="blocage-raison-input"
            />
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="blocage-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            Appliquer le blocage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
